// Local, isolated sandbox state for the Builder2 prototype.
// Deliberately separate from websiteBuilderStore.js — nothing here touches
// the DB, saveWebsite(), or admin website loading. Reload resets to the
// seeded starting page.
import { reactive } from 'vue'
import { blockRegistry, blockOrder } from './blockRegistry'

function cloneContent(value) {
  return JSON.parse(JSON.stringify(value))
}

function makeId() {
  return `blk_${Math.random().toString(36).slice(2, 10)}`
}

export const page2 = reactive({ blocks: [] })

// Real garden03 copy (backend/routes/templates.js) used only to seed the
// initial demo page — a nicer starting point than the registry's generic
// placeholder content.
const garden03Content = {
  nav: {
    nav_logo: { type: 'image', label: 'Logo', value: 'Logo' },
    nav_links: {
      type: 'array:text',
      label: 'Navigation Links',
      value: ['Home', 'Services', 'About', 'Gallery', 'Testimonials', 'Contact'],
    },
  },
  'hero-video': {
    hero_video_url: { type: 'video', label: 'Hero Video', value: 'https://www.pexels.com/download/video/35083678/' },
    hero_kicker: { type: 'text', label: 'Kicker', value: 'Professional Gardening & Landscaping Services' },
    hero_headline: { type: 'text', label: 'Headline', value: 'Extend Your Home With a Beautiful Garden' },
    hero_sub: { type: 'textarea', label: 'Subheading', value: 'We craft outdoor spaces that blend beauty, function, and nature — tailored to your property.' },
    hero_btn_1: { type: 'text', label: 'Primary Button', value: 'Our Services' },
    hero_btn_2: { type: 'text', label: 'Secondary Button', value: 'Request a Quote' },
  },
  services: {
    label: { type: 'text', label: 'Section Label', value: 'What We Do' },
    title: { type: 'text', label: 'Section Title', value: 'Expert Services For Every Garden' },
    sub: { type: 'textarea', label: 'Subheading', value: 'From a single hedge trim to a full landscape transformation, we bring the same care and craft to every project.' },
    items: {
      type: 'array:object',
      label: 'Service Cards',
      value: [
        { icon: 'mdi-content-cut', title: 'Hedge Trimming', description: 'Professional hedge and shrub trimming to keep your property neat, healthy, and well maintained.' },
        { icon: 'mdi-tree', title: 'Tree Trimming', description: 'Pruning and shaping services that improve tree health, appearance, and long-term growth.' },
        { icon: 'mdi-sprout', title: 'Landscape Installation', description: 'Garden bed weeding, mulch installation, soil placement, lawn top dressing, and seeding services.' },
      ],
    },
  },
  pricing: {
    label: { type: 'text', label: 'Section Label', value: 'Materials & Delivery' },
    title: { type: 'text', label: 'Section Title', value: 'Quality Soil & Mulch Delivered to Ottawa & Surrounding Areas' },
    sub: { type: 'textarea', label: 'Subheading', value: "Looking to upgrade your garden or landscaping? We've got you covered with reliable delivery and quality materials." },
    items: {
      type: 'array:object',
      label: 'Pricing Items',
      value: [
        { name: 'Topsoil', price: '$130', note: '' },
        { name: 'Garden Soil', price: '$140', note: '' },
        { name: 'Mulch', price: 'From $170', note: 'Ask us about available types!' },
      ],
    },
    footer_note: { type: 'textarea', label: 'Footer Note', value: 'Delivery available across Ottawa and nearby areas. Bulk orders available in select areas — get in touch for an exact quote.' },
  },
  about: {
    label: { type: 'text', label: 'Section Label', value: 'About Us' },
    title: { type: 'text', label: 'Section Title', value: 'Growing Beautiful Spaces' },
    body_1: { type: 'textarea', label: 'Body Paragraph 1', value: "We're a family-run landscaping company based in the Ottawa area. We've been helping homeowners and businesses create outdoor spaces that feel like an extension of their interior life." },
    body_2: { type: 'textarea', label: 'Body Paragraph 2', value: 'Every project starts with a conversation. We listen, we plan, and we execute with a level of attention to detail that sets us apart.' },
    points: {
      type: 'array:text',
      label: 'Key Points',
      value: ['Licensed & insured landscape professionals', 'Eco-friendly products and practices', 'Free consultation', 'Custom Quotes'],
    },
    image: { type: 'image', label: 'About Image', value: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=700&q=80' },
    badge_number: { type: 'text', label: 'Badge Number', value: '7+' },
    badge_label: { type: 'text', label: 'Badge Label', value: 'Years of Experience' },
  },
  gallery: {
    label: { type: 'text', label: 'Section Label', value: 'Our Work' },
    title: { type: 'text', label: 'Section Title', value: 'A Few Past Projects' },
    images: {
      type: 'array:object',
      label: 'Gallery Images',
      value: [
        { url: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=600&q=80', caption: 'Backyard garden renovation', size: 'tall' },
        { url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80', caption: 'Modern patio design', size: '' },
        { url: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&q=80', caption: 'Wildflower meadow', size: '' },
        { url: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80', caption: 'Raised bed vegetable garden', size: 'wide' },
      ],
    },
  },
  testimonials: {
    label: { type: 'text', label: 'Section Label', value: 'Client Stories' },
    title: { type: 'text', label: 'Section Title', value: 'What Our Clients Say' },
    items: {
      type: 'array:object',
      label: 'Testimonials',
      value: [
        { name: 'Sarah Mitchell', location: 'Toronto, ON', quote: 'Gardenhub completely transformed our backyard. The design was thoughtful and they finished ahead of schedule.' },
        { name: 'David Okafor', location: 'Mississauga, ON', quote: "I've used three different landscaping companies over the years. None come close to Gardenhub's attention to detail." },
      ],
    },
  },
  contact: {
    label: { type: 'text', label: 'Section Label', value: 'Get In Touch' },
    title: { type: 'text', label: 'Section Title', value: "Let's Build Something Beautiful" },
    sub: { type: 'textarea', label: 'Subheading', value: "Ready to get started? Send us a message and we'll get back to you within one business day with a free quote." },
    email: { type: 'email', label: 'Email', value: 'contact@gardenhub.com' },
    phone: { type: 'text', label: 'Phone', value: 'Toll Free: +1 212-212-2376' },
    hours: { type: 'text', label: 'Hours', value: 'Mon – Fri: 9:00 – 17:00' },
  },
  footer: {
    footer_logo: { type: 'text', label: 'Logo', value: 'Logo2' },
    tagline: { type: 'textarea', label: 'Tagline', value: 'Professional landscaping and garden services across the Greater Toronto Area.' },
    footer_disclaimer: { type: 'text', label: 'Disclaimer', value: '© Copyright TY Landscaping 2026. All rights reserved.' },
  },
}

export function seedFromGarden03() {
  page2.blocks.splice(
    0,
    page2.blocks.length,
    ...blockOrder.map(type => ({
      id: makeId(),
      type,
      variant: blockRegistry[type].defaultVariant ?? 'default',
      content: cloneContent(garden03Content[type] ?? blockRegistry[type].defaultContent),
    }))
  )
}

export function addBlock(type, afterIndex = page2.blocks.length - 1) {
  const entry = blockRegistry[type]
  if (!entry) return null

  const block = {
    id: makeId(),
    type,
    variant: entry.defaultVariant ?? 'default',
    content: cloneContent(entry.defaultContent),
  }

  page2.blocks.splice(afterIndex + 1, 0, block)
  return block
}

export function removeBlock(id) {
  const index = page2.blocks.findIndex(b => b.id === id)
  if (index !== -1) page2.blocks.splice(index, 1)
}

export function moveBlock(id, direction) {
  const index = page2.blocks.findIndex(b => b.id === id)
  if (index === -1) return

  const target = index + direction
  if (target < 0 || target >= page2.blocks.length) return

  const [block] = page2.blocks.splice(index, 1)
  page2.blocks.splice(target, 0, block)
}
